import { ApiError, get, post, put, del } from './api';
import type {
  BatchExtraction,
  BatchExtractionListResponse,
  BatchExtractionStatusResponse,
  UploadUrlResponse,
  CreateBatchRequest,
  UpdateRecordsRequest,
  CreateManualRecordRequest,
  UpdateTeamsRequest,
  DistributeRequest,
  DistributionResult,
  JucoActionResponse,
  JucoScope,
  BatchDistribution,
  BatchExtractionRecord,
  PageImageUrlResponse,
  PageTeamNameUpdate,
  BatchCommitValidationResult,
  SetRecordsJucoRequest,
  FillRecordsJucoYearRequest,
} from '../types/batch-extraction';

const BASE_URL = '/api/admin/batch-extractions';

/**
 * Get paginated list of batch extractions
 */
export async function getBatchExtractions(
  pageNumber = 1,
  pageSize = 20
): Promise<BatchExtractionListResponse> {
  const params = new URLSearchParams({
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
  });
  return get<BatchExtractionListResponse>(`${BASE_URL}?${params}`);
}

/**
 * Create a new batch extraction and get upload URL
 */
export async function createBatchExtraction(
  request: CreateBatchRequest
): Promise<UploadUrlResponse> {
  return post<UploadUrlResponse>(BASE_URL, request);
}

/**
 * Get batch extraction details with pages and records
 */
export async function getBatchExtraction(id: string): Promise<BatchExtraction> {
  return get<BatchExtraction>(`${BASE_URL}/${id}`);
}

/**
 * Get batch extraction status for polling
 */
export async function getBatchExtractionStatus(
  id: string
): Promise<BatchExtractionStatusResponse> {
  return get<BatchExtractionStatusResponse>(`${BASE_URL}/${id}/status`);
}

/**
 * Upload PDF to the signed URL
 */
export async function uploadPdf(uploadUrl: string, file: File): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': 'application/pdf',
    },
  });

  if (!response.ok) {
    throw new Error(`Upload failed: ${response.statusText}`);
  }
}

/**
 * Start PDF processing (split and extract)
 */
export async function processPdf(
  id: string
): Promise<BatchExtractionStatusResponse> {
  return post<BatchExtractionStatusResponse>(`${BASE_URL}/${id}/process`, {});
}

/**
 * Retry extraction for a failed page
 */
export async function retryPage(batchId: string, pageId: string): Promise<void> {
  await post(`${BASE_URL}/${batchId}/pages/${pageId}/retry`, {});
}

/**
 * Delete a page and its associated records
 */
export async function deletePage(batchId: string, pageId: string): Promise<void> {
  await del(`${BASE_URL}/${batchId}/pages/${pageId}`);
}

/**
 * Retry extraction for all failed pages in a batch
 */
export async function retryExtraction(
  batchId: string
): Promise<BatchExtractionStatusResponse> {
  return post<BatchExtractionStatusResponse>(`${BASE_URL}/${batchId}/retry`, {});
}

/**
 * Bulk update records
 */
export async function updateRecords(
  batchId: string,
  request: UpdateRecordsRequest
): Promise<void> {
  await put(`${BASE_URL}/${batchId}/records`, request);
}

/**
 * Mark a set of records as JUCO, or clear the marking.
 *
 * Per team, not per batch: a scout book of junior colleges can contain a prep program, and a
 * batch-level flag would misclassify it. The server also re-infers each record's graduation year
 * on the two-year ladder, which is the part that cannot be done client-side.
 */
export async function setRecordsJuco(
  batchId: string,
  request: SetRecordsJucoRequest
): Promise<number> {
  const response = await put<JucoActionResponse>(`${BASE_URL}/${batchId}/records/juco`, request);
  assertScopeApplied(request.scope, response);
  return response?.updated ?? 0;
}

/**
 * Fill one JC year across many records at once.
 *
 * Required rather than a convenience: a roster page with no class column at all leaves every row
 * blank, and marking it JUCO would otherwise block the import permanently.
 */
export async function fillRecordsJucoYear(
  batchId: string,
  request: FillRecordsJucoYearRequest
): Promise<number> {
  const response = await put<JucoActionResponse>(`${BASE_URL}/${batchId}/records/juco-year`, request);
  assertScopeApplied(request.scope, response);
  return response?.filled ?? 0;
}

/**
 * Deploy skew guard. An API older than the batch scope ignores `scope`, reads the empty id list,
 * changes nothing and still answers 200. Without this the reviewer would see "done" on a batch
 * that was never marked.
 */
function assertScopeApplied(requested: JucoScope | undefined, response: JucoActionResponse | undefined) {
  if (requested === 'batch' && response?.scope !== 'batch') {
    throw new JucoScopeUnsupportedError();
  }
}

/** The API answered a batch-wide JUCO request without applying it, so it predates the scope. */
export class JucoScopeUnsupportedError extends Error {
  constructor() {
    super('The server cannot update all pages at once yet. Mark each page instead.');
    this.name = 'JucoScopeUnsupportedError';
  }
}

/**
 * Words for a failed JUCO action. The two failures a reviewer can act on (a page still extracting,
 * or an older server) say what to do; anything else gets the caller's fallback.
 */
export function describeJucoActionError(err: unknown, fallback: string): string {
  if (err instanceof JucoScopeUnsupportedError) return err.message;
  if (err instanceof ApiError && err.statusCode === 409) {
    return err.message || 'Some pages are still extracting. Try again once every page has finished.';
  }
  return fallback;
}

/**
 * Add a manual record
 */
export async function addManualRecord(
  batchId: string,
  request: CreateManualRecordRequest
): Promise<BatchExtractionRecord> {
  return post<BatchExtractionRecord>(`${BASE_URL}/${batchId}/records`, request);
}

/**
 * Delete a record
 */
export async function deleteRecord(
  batchId: string,
  recordId: string
): Promise<void> {
  await del(`${BASE_URL}/${batchId}/records/${recordId}`);
}

/**
 * Update team names
 */
export async function updateTeams(
  batchId: string,
  request: UpdateTeamsRequest
): Promise<void> {
  await put(`${BASE_URL}/${batchId}/teams`, request);
}

/**
 * Update default team name for the entire batch
 */
export async function updateDefaultTeamName(
  batchId: string,
  defaultTeamName: string | null
): Promise<void> {
  await put(`${BASE_URL}/${batchId}/default-team-name`, { defaultTeamName });
}

/**
 * Update team name for a specific page
 */
export async function updatePageTeamName(
  batchId: string,
  pageId: string,
  teamName: string | null
): Promise<void> {
  await put(`${BASE_URL}/${batchId}/pages/${pageId}/team-name`, { teamName });
}

/**
 * Bulk update team names across multiple pages in a single transaction.
 * Rejects the request if any pageId does not belong to the batch.
 */
export async function updatePageTeamNames(
  batchId: string,
  updates: PageTeamNameUpdate[]
): Promise<BatchExtractionStatusResponse> {
  return put<BatchExtractionStatusResponse>(
    `${BASE_URL}/${batchId}/pages/team-names`,
    { updates }
  );
}

/**
 * Pre-flight check that every committable record resolves to exactly one team in
 * the target organization [SpecFlow C5]. Drives the commit gate before
 * distribution. Returns 200 with `isValid` + any `unresolvedRecordIds`; never
 * commits anything.
 */
export async function validateCommit(
  batchId: string,
  organizationId: string
): Promise<BatchCommitValidationResult> {
  return post<BatchCommitValidationResult>(
    `${BASE_URL}/${batchId}/validate-commit`,
    { organizationId }
  );
}

/**
 * Distribute batch to organizations.
 *
 * Rejected with a `400` (surfaced as an `ApiError` whose `response` is a
 * `DistributeRejection`) if any target-org record is unresolved — nothing is
 * committed in that case.
 */
export async function distribute(
  batchId: string,
  request: DistributeRequest
): Promise<DistributionResult> {
  return post<DistributionResult>(`${BASE_URL}/${batchId}/distribute`, request);
}

/**
 * Get distributions for a batch
 */
export async function getDistributions(
  batchId: string
): Promise<BatchDistribution[]> {
  return get<BatchDistribution[]>(`${BASE_URL}/${batchId}/distributions`);
}

/**
 * Delete a batch extraction
 */
export async function deleteBatchExtraction(id: string): Promise<void> {
  await del(`${BASE_URL}/${id}`);
}

/**
 * Get signed URL for a page image
 */
export async function getPageImageUrl(
  batchId: string,
  pageId: string,
  pageNumber: number
): Promise<PageImageUrlResponse> {
  const params = new URLSearchParams({ pageNumber: String(pageNumber) });
  return get<PageImageUrlResponse>(
    `${BASE_URL}/${batchId}/pages/${pageId}/image-url?${params}`
  );
}
